import type { PlanungBoard } from "@/lib/montageplanung";
import { api } from "@/lib/apiClient";
import { publicFetch } from "@/lib/publicApiClient";

export type DisplayDeviceStatus = "pending" | "approved" | "rejected" | "revoked";
export type DisplayThemeMode = "dark" | "light" | "system";

export interface DisplayDeviceStatusResponse {
  device_id: string;
  status: DisplayDeviceStatus;
  device_label?: string | null;
  theme_mode?: DisplayThemeMode;
  ui_scale?: number;
  requested_at?: string | null;
  approved_at?: string | null;
}

export interface DisplayDevice {
  id: number;
  device_id: string;
  device_label?: string | null;
  user_agent?: string | null;
  status: DisplayDeviceStatus;
  theme_mode?: DisplayThemeMode;
  ui_scale?: number;
  requested_at?: string | null;
  last_seen_at?: string | null;
  approved_at?: string | null;
  approved_by_login?: string | null;
  approved_by_display?: string | null;
  rejected_at?: string | null;
  rejected_by_login?: string | null;
  rejected_by_display?: string | null;
}

export interface DisplayDeviceListResponse {
  items: DisplayDevice[];
  pending_count: number;
}

const DEVICE_STORAGE_KEY = "eh-mp-display-device-id";

export function getOrCreateDisplayDeviceId(): string {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(DEVICE_STORAGE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    window.localStorage.setItem(DEVICE_STORAGE_KEY, id);
  }
  return id;
}

export function registerDisplayDevice(payload: {
  device_id: string;
  device_label?: string;
  user_agent?: string;
}): Promise<DisplayDeviceStatusResponse> {
  return publicFetch("/public/montageplan/display/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getDisplayDeviceStatus(deviceId: string): Promise<DisplayDeviceStatusResponse> {
  return publicFetch(
    `/public/montageplan/display/status?device_id=${encodeURIComponent(deviceId)}`,
  );
}

export function getDisplayBoard(params: {
  device_id: string;
  date_from: string;
  date_to: string;
}): Promise<PlanungBoard> {
  const q = new URLSearchParams({
    device_id: params.device_id,
    date_from: params.date_from,
    date_to: params.date_to,
  });
  return publicFetch(`/public/montageplan/display/board?${q.toString()}`);
}

export function listDisplayDevices(): Promise<DisplayDeviceListResponse> {
  return api.get("/montageplanung/display-devices");
}

export function approveDisplayDevice(id: number): Promise<DisplayDevice> {
  return api.post(`/montageplanung/display-devices/${id}/approve`, {});
}

export function rejectDisplayDevice(id: number): Promise<DisplayDevice> {
  return api.post(`/montageplanung/display-devices/${id}/reject`, {});
}

export function revokeDisplayDevice(id: number): Promise<DisplayDevice> {
  return api.post(`/montageplanung/display-devices/${id}/revoke`, {});
}

export function broadcastDisplayAppUpdate(): Promise<{ ok: boolean }> {
  return api.post("/montageplanung/display-devices/broadcast-app-update", {});
}

export function updateDisplayDevice(
  id: number,
  payload: {
    device_label?: string | null;
    theme_mode?: DisplayThemeMode;
    ui_scale?: number;
  },
): Promise<DisplayDevice> {
  return api.patch(`/montageplanung/display-devices/${id}`, payload);
}

/** @deprecated Use updateDisplayDevice */
export function updateDisplayDeviceLabel(
  id: number,
  device_label: string | null,
): Promise<DisplayDevice> {
  return updateDisplayDevice(id, { device_label });
}
